import { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import api from "../../../../../Axios";
import { toast } from "react-toastify";
import { TextField, Button, Typography, Box } from "@mui/material";
import useConfirm from "../../../../../hooks/useConfirm";

export default function Step3Modules({
  programId,
  initialData,
  setMasterRender,
  next,
  prev,
}) {
  const [loading, setLoading] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState(null);

  const [module, setModule] = useState({
    title: "",
    slug: "",
    earn_points: 0,
    description: "",
  });
  const [errors, setErrors] = useState({});

  const [modules, setModules] = useState([]);

  useEffect(() => {
    if (initialData && Array.isArray(initialData.modules)) {
      setModules(initialData.modules);
    }
  }, [initialData]);

  const isValidSlug = (value) => /^[a-z0-9-]+$/.test(value);

  const validateForm = () => {

    const validPattern = /^[A-Za-z0-9 _-]+$/;
    let newErrors = {};
    if (!module.title.trim()) newErrors.title = "Module Title is required";
    if (!module.slug.trim()) newErrors.slug = "Module Slug is required";
    if (!isValidSlug(module.slug.trim())) newErrors.slug = "Module Slug is requiredOnly lowercase letters, numbers, and hyphens allowed";
    if (!validPattern.test(module.title.trim())) newErrors.title = "Only letters, numbers, hyphen (-) and underscore"

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addModule = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        program_id: programId,
        title: module.title,
        slug: module.slug,
        earn_points: module.earn_points,
        description: module.description,
      };

      const res = await api.post("/lnd/module", payload);
      const savedModule = res.data?.data?.module || payload;

      setModules((prev) => [...prev, savedModule]);
      setMasterRender((prev) => !prev);

      resetForm();
      toast.success("Module added successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add module");
    } finally {
      setLoading(false);
    }
  };

  const updateModule = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        program_id: programId,
        title: module.title,
        earn_points: module.earn_points,
        description: module.description,
      };

      // slug may also be needed in update payload ideally, adding it conservatively
      if (module.slug) payload.slug = module.slug;

      await api.put(`/lnd/update-modules/${editingModuleId}`, payload);

      setModules((prev) =>
        prev.map((m) =>
          m.id === editingModuleId ? { ...m, ...payload } : m
        )
      );

      toast.success("Module updated successfully");
      setMasterRender((prev) => !prev);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update module");
    } finally {
      setLoading(false);
    }
  };

  const { confirm } = useConfirm();
  const deleteModule = async (moduleId) => {
    try {
      const isConfirmed = await confirm({
        title: "Confirm Module Deletion",
        content: `Are you sure you want to delete this module?`,
        confirmText: "Yes, Delete",
        cancelText: "Cancel"
      });
      if (!isConfirmed) return;

      await api.delete(`/lnd/delete-module/${moduleId}`);

      setModules((prev) => prev.filter((m) => m.id !== moduleId));
      setMasterRender((prev) => !prev);

      toast.success("Module deleted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete module");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedModules = items.map((item, index) => ({
      ...item,
      sequence_order: index + 1,
    }));

    setModules(updatedModules);

    try {
      await api.post("/lnd/update-module-sequence", {
        modules: updatedModules.map((m) => ({
          id: m.id,
          sequence_order: m.sequence_order,
        })),
      });

      setMasterRender((prev) => !prev);
      toast.success("Sequence updated");
    } catch (err) {
      toast.error("Failed to update sequence");
    }
  };

  const handleEdit = (mod) => {
    setEditingModuleId(mod.id);
    setModule({
      title: mod.title || "",
      slug: mod.slug || "",
      earn_points: mod.earn_points || 0,
      description: mod.description || "",
    });
  };

  const resetForm = () => {
    setModule({ title: "", slug: "", earn_points: 0, description: "" });
    setErrors({});
    setEditingModuleId(null);
  };

  return (
    <div className="container-fluid">
      <div className="row">

        {/* LEFT FORM */}
        <div className="col-md-7" key={editingModuleId ?? 'create'}>

          <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
            <Typography variant="h6" className="fw-bold" sx={{ mb: 4 }}>
              {editingModuleId ? "Edit Module" : "Add Module"}
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Module Title *"
              placeholder="Enter module title"
              id="title"
              value={module.title}
              onChange={(e) => {
                setModule({ ...module, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Slug (e.g. program-name) *"
              placeholder="Enter module slug"
              id="slug"
              value={module.slug}
              onChange={(e) => {
                setModule({ ...module, slug: e.target.value });
                if (errors.slug) setErrors({ ...errors, slug: "" });
              }}
              error={!!errors.slug}
              helperText={errors.slug}
              sx={{ mb: 3 }}
            />

            <Typography sx={{ mb: 1 }}>
              Module Description
            </Typography>

            <CKEditor
              editor={ClassicEditor}
              data={module.description}
              config={{
                mediaEmbed: {
                  previewsInData: true
                }
              }}
              onChange={(event, editor) => {
                const data = editor.getData();
                setModule({ ...module, description: data });
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 4
              }}
            >

              <div className="d-flex justify-content-end w-100 gap-3">
                {editingModuleId && (
                  <button className="btn btn-secondary px-4 fw-medium" onClick={resetForm}>
                    Cancel Edit
                  </button>
                )}
                <button
                  className="btn btn-success px-4 fw-medium"
                  onClick={editingModuleId ? updateModule : addModule}
                  disabled={loading}
                >
                  {editingModuleId ? "Update Module" : "Add Module"}
                </button>
              </div>

            </Box>
          </div>

          <div className="d-flex justify-content-between mt-4 px-2">
            <button className="btn btn-secondary px-4 fw-medium" onClick={prev}>
              Previous
            </button>

            <button
              className="btn btn-success px-4 fw-medium"
              onClick={next}
              disabled={modules.length === 0}
            >
              Save & Continue
            </button>
          </div>

        </div>

        {/* RIGHT LIST */}
        <div className="col-md-5">
          <h5 className="mb-3">Added Modules</h5>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="modules">
              {(provided) => (
                <ul
                  className="list-group"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  {modules.map((mod, index) => (
                    <Draggable
                      key={mod.id}
                      draggableId={String(mod.id)}
                      index={index}
                    >
                      {(provided) => (
                        <li
                          className="list-group-item d-flex justify-content-between align-items-start"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <div className="d-flex gap-3">

                            {/* Drag Icon */}
                            <span
                              {...provided.dragHandleProps}
                              style={{ cursor: "grab" }}
                            >
                              <GripVertical size={18} />
                            </span>

                            <div>
                              <strong>
                                {index + 1}. {mod.title}
                              </strong>
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleEdit(mod)}
                            >
                              Edit
                            </button>

                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteModule(mod.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </div>

      </div>
    </div>
  );
}